'use client';

import { memo, useState } from 'react';
import {
  BellIcon, EllipsisVerticalIcon, MapIcon, PencilIcon, TrashIcon,
} from '@heroicons/react/24/outline';
import { useTranslation } from 'next-i18next';
import { Anchor } from '@components/common';
import { getI18nLinkHref } from '@services/i18nPaths';
import { slugify } from '@utils/strings/slugify';
import { cn } from '@utils/cn';
import type { JobSearchQueryParams } from '@type/filters';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@components/ui/dropdown-menu';

interface CompactJobAlertCardProps {
  title?: string;
  isSelected?: boolean;
  onSelect?: () => void;
  onEdit?: () => void;
  editHref?: string;
  /**
   * Callback for the MAP action button. Receives query params derived from the card's data.
   * Provided by a parent outside the import cycle (e.g. MissionControl).
   */
  onOpenMap?: (queryParams?: Partial<JobSearchQueryParams>) => void;
  /**
   * Overrides the query params passed to {@link onOpenMap}. Needed when the card's `title` is not
   * a keyword (e.g. a location interest, whose value belongs in the `location` param, not `keyword`).
   */
  mapQueryParams?: Partial<JobSearchQueryParams>;
  /** Callback for the DELETE action. When provided, a "Delete Job" entry is appended to the menu. */
  onDelete?: () => void;
  locale: Locale;
  /** Overrides the computed href. Useful when the card should link outside the MC context (e.g. guest users). */
  href?: string;
  /** Location string for non-alert cards (e.g. last experience). Alert cards derive location from their model. */
  location?: string;
}

const TRIGGER_BTN_CLASS = 'flex size-8 items-center justify-center rounded-2xl text-ink opacity-0 transition hover:bg-surface-300 group-hover:opacity-100 data-[state=open]:bg-surface-300 data-[state=open]:opacity-100';
const MENU_ITEM_CLASS = 'cursor-pointer gap-2 px-3 py-2 text-sm text-ink hover:!bg-surface-200 focus:!bg-surface-200 focus:text-ink';

/**
 * @description Compact candidate sidebar view — used for job alerts, last experience, and next-job suggestions.
 * Shows the title and a "more" menu with MAP, EDIT, and NEW actions.
 */
const CompactJobAlertCard = (props: CompactJobAlertCardProps) => {
  const {
    title, isSelected, onSelect, onEdit, editHref, onOpenMap, mapQueryParams, onDelete, locale,
    href: hrefOverride, location: locationProp,
  } = props;
  const { t } = useTranslation('common');

  const getAlertTitle = () => title || t('job-alerts.default-title') || 'Job Alert';

  const cardTitle = getAlertTitle();
  const hoverTitle = locationProp ? `${cardTitle}\n${locationProp}` : cardTitle;
  const newJobsLower = t('job-label-newjobs').toLowerCase();
  const newJobsTitle = newJobsLower.charAt(0).toUpperCase() + newJobsLower.slice(1);

  const href = hrefOverride ?? (() => {
    const keyword = title;
    const loc = locationProp;

    if (loc) {
      const locationSlug = slugify(loc);
      const keywordSlug = keyword ? `/${slugify(keyword)}` : '';
      return getI18nLinkHref(`/mc/job/list/location-${locationSlug}${keywordSlug}`, locale);
    }
    if (keyword) {
      return getI18nLinkHref(`/mc/job/list/${slugify(keyword)}`, locale);
    }
    return getI18nLinkHref('/mc', locale);
  })();

  const mapLocation = locationProp;
  const [menuOpen, setMenuOpen] = useState(false);

  const handleOpenMap = () => {
    if (mapQueryParams) {
      onOpenMap?.(mapQueryParams);
      return;
    }
    const keyword = title;
    onOpenMap?.({
      ...(keyword && { keyword }),
      ...(mapLocation && { location: [mapLocation] }),
    });
  };

  return (
    <div
      className={cn(
        'group flex items-center gap-2 rounded-2xl px-3 py-1 transition-colors hover:bg-surface-150',
        isSelected && 'bg-primary/5',
      )}
    >
      {/* Title — fills remaining space so the trigger right-aligns; truncates with ellipsis */}
      <Anchor
        href={href}
        styling="none"
        className="min-w-0 flex-1"
        title={hoverTitle}
        onClick={(e) => {
          if (onSelect) {
            e.preventDefault();
            onSelect();
          }
        }}
      >
        <span
          className={cn(
            'block truncate text-sm text-ink',
            isSelected && 'text-primary',
          )}
        >
          {cardTitle}
        </span>
      </Anchor>

      {/* "More" menu — folds map / edit / bell into a single popover so the card stays compact.
       * Controlled so each action explicitly closes the popup; Radix's auto-close is blocked by
       * the preventDefault/stopPropagation we need on the inner handlers. */}
      <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label={t('global.label.actions')}
            title={t('global.label.actions')}
            className={TRIGGER_BTN_CLASS}
            onClick={(e) => e.stopPropagation()}
          >
            <EllipsisVerticalIcon className="size-5" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" side="bottom" sideOffset={4} className="z-[90] min-w-48">
          {onOpenMap && (
            <DropdownMenuItem
              className={MENU_ITEM_CLASS}
              onSelect={(e) => { e.preventDefault(); setMenuOpen(false); handleOpenMap(); }}
            >
              <MapIcon className="size-5 text-ink" />
              <span>{t('job.list.on.map')}</span>
            </DropdownMenuItem>
          )}

          {onEdit && (
            <DropdownMenuItem asChild className={MENU_ITEM_CLASS}>
              <Anchor
                href={editHref ?? '#'}
                styling="none"
                onClick={(e) => { e.stopPropagation(); e.preventDefault(); setMenuOpen(false); onEdit(); }}
              >
                <PencilIcon className="size-5 text-ink" />
                <span>{t('job.edit.title')}</span>
              </Anchor>
            </DropdownMenuItem>
          )}

          {/* Bell entry — same action as clicking the title */}
          <DropdownMenuItem asChild className={MENU_ITEM_CLASS}>
            <Anchor
              href={href}
              styling="none"
              onClick={(e) => {
                if (onSelect) {
                  e.preventDefault();
                  setMenuOpen(false);
                  onSelect();
                }
              }}
            >
              <BellIcon className="size-5 text-ink" />
              <span>{newJobsTitle}</span>
            </Anchor>
          </DropdownMenuItem>

          {onDelete && (
            <DropdownMenuItem
              className={MENU_ITEM_CLASS}
              onSelect={(e) => { e.preventDefault(); setMenuOpen(false); onDelete(); }}
            >
              <TrashIcon className="size-5 text-ink" />
              <span>{t('label.delete.job')}</span>
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export const MemoizedCompactJobAlertCard = memo(CompactJobAlertCard);
